// @flow
import React from 'react'
import moment from 'moment'
import LandingIcon from 'mz-icons/airplane-landing'
import { FormattedMessage, FormattedMoment } from 'mz-intl'
import WaitingTimeStartsIcon from './assets/waiting-time-starts'
import WaitingTimeEndsIcon from './assets/waiting-time-ends'
import messages from './messages.intl'
import {
  Dot,
  DotWithLine,
  Line,
  Timeline,
  Step,
  Time,
  Icon,
  Icons,
  Text,
  TextItem,
} from './elements'

// Types
type Props = {
  pickupPoint: string,
  landingTime: moment,
  driverArrivesTime: moment,
  freeWaitingMinutes: number,
  appearance?: 'primary' | 'inverted' | 'return',
}

// Utils
const TIME_FORMAT = 'hh:mm A'

/**
 * This function helps to create a final steps list where all given steps
 * properly ordered and steps with same time merged to one step
 * @param  {Object[]} steps
 * @return {Object[]}
 */
function mergeStepsWithSameTime(steps) {
  return steps
    .slice()
    .sort((a, b) => a.timeMoment.diff(b.timeMoment))
    .reduce((acc, x, i) => {
      if (i === 0) return [{ ...x, grow: 1 }]

      const prevStep = acc[acc.length - 1]
      const minutesDiff = prevStep.timeMoment.diff(x.timeMoment, 'minutes')
      const grow = Math.max(1, Math.abs(Math.floor(minutesDiff * 0.1)))

      if (prevStep.timeMoment.isSame(x.timeMoment)) {
        return [
          ...acc.slice(0, acc.length - 1),
          {
            ...x,
            grow,
            icons: [...prevStep.icons, ...x.icons],
            titles: [...prevStep.titles, ...x.titles],
          },
        ]
      }
      return [...acc, { ...x, grow }]
    }, [])
}

/*
 * This components mixes Landing Step with Driver Step.
 * Used when flight and driver arrives at the same time
 */
const BasicStep = ({ timeMoment, appearance, icons, titles }) => {
  const manyDots = titles.length > 1
  return (
    <Step>
      <Icons>
        {icons.map((IconComponent, i) => (
          <Icon $grouped={manyDots} key={i}>
            <IconComponent $fixedWidth />
          </Icon>
        ))}
      </Icons>
      <DotWithLine>
        <Line $appearance={appearance} />
        {titles.map((x, i) => (
          <Dot key={`${i}-dot`} $grouped={manyDots} />
        ))}
      </DotWithLine>
      <FormattedMoment tagName={Time} value={timeMoment} format={TIME_FORMAT} />
      <Text>
        {titles.map((m, i) => (
          <FormattedMessage tagName={TextItem} key={i} {...m} />
        ))}
      </Text>
    </Step>
  )
}

const StepLine = ({ appearance, grow }) => (
  <Step $grow={grow}>
    <Icons />
    <DotWithLine>
      <Line $appearance={appearance} />
    </DotWithLine>
  </Step>
)

/**
 * A component that helps the user to understand what is a timeline of his pickup.
 * It shows three points: landing time, driver arrival time and free waiting time
 * expiary time, properly ordered. So the user will understand his pickup parameters
 * better and will know what time he will have to grab his laggage before free
 * waiting time will end
 * @param {moment} landingTime
 * @param {moment} driverArrivesTime
 * @param {integre} freeWaitingMinutes
 * @param {String} appearance
 */
const PickupTimeline = ({
  landingTime,
  pickupPoint,
  driverArrivesTime,
  freeWaitingMinutes,
  appearance = 'primary',
}: Props) => {
  const freeTimeEndsTime = moment(driverArrivesTime).add(
    freeWaitingMinutes,
    'minutes'
  )
  const finalSteps = mergeStepsWithSameTime([
    {
      timeMoment: landingTime.clone().startOf('minutes'),
      icons: [LandingIcon],
      titles: [{ message: messages.LANDING_TIME, values: { pickupPoint } }],
    },
    {
      timeMoment: driverArrivesTime.clone().startOf('minutes'),
      icons: [WaitingTimeStartsIcon],
      titles: [{ message: messages.DRIVER_ARRIVES }],
    },
    {
      timeMoment: freeTimeEndsTime.clone().startOf('minutes'),
      icons: [WaitingTimeEndsIcon],
      titles: [{ message: messages.WAITING_TIME_ENDS }],
    },
  ])

  return (
    <Timeline>
      {finalSteps.map((x, i) => [
        i > 0 && (
          <StepLine key={`${i}-line`} appearance={appearance} grow={x.grow} />
        ),
        <BasicStep {...x} key={`${i}-step`} appearance={appearance} />,
      ])}
      {finalSteps.length === 1 && <StepLine appearance={appearance} grow={5} />}
    </Timeline>
  )
}

export default PickupTimeline
